import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import moment from 'moment';
import classnames from 'classnames';
import MetricsCardWrapTitle from '@/pages/dashboard/components/MetricsCardWrap/MetricsCardWrapTitle';
import { getLanguage } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { commonDateDefault } from '@/pages/dashboard/utils/dateUtils';
import "./index.scss";
interface MetricsCardWrapTitleProps {
  icon?: string;
  imgUrl?: string; // 图片地址
  tips?: string;
  isJumpable?: boolean;
  // 是否开启icon的info提示（与标题hover的提示互斥）
  isIconTips?: boolean;
  title?: React.ReactNode;
  titleEn?: string;
  titleCn?: string;
  subTitleEn?: string;
  subTitleCn?: string;
  className?: string;
  style?: React.CSSProperties;
  imgClassName?: string;
  children?: any;
  timeType?: 'Day' | 'Week' | 'Month';
  currentGame?: any;
  filters?: any;
  lastDate?: any;
  startNum?: number;
  endNum?: number;
  payType?: string;
}

const getDateDefault = (params: any): [moment.Moment, moment.Moment] | string => {
  const { timeType = 'day', timeZone, isSingle = false, startNum = 1, endNum = 1, singleNum = 1, lastDate, isLastDateMonth = false, lastDateStartNum = 1, lastDateEndNum = 1, daySub = 1 } = params || {};
  // 用两个变量是因为起始值如果进行了subtract，会改变原有值，如果结束的值用同一个变量，会产生深拷贝问题
  // 偏移时区过后格式转换，解决week粒度下23:59:59TZ被转换成第二天
  const currentStartDate = moment(lastDate).isValid() ? moment(lastDate) : moment(moment().subtract(daySub, 'days').utcOffset(timeZone).format('YYYY-MM-DD HH:mm:ss')).startOf('day');
  const currentEndDate = moment(lastDate).isValid() ? moment(lastDate) : moment(moment().subtract(daySub, 'days').utcOffset(timeZone).format('YYYY-MM-DD HH:mm:ss')).startOf('day');
  if (timeType?.toLocaleLowerCase() === 'week') {
    // 不存在week和month的isSingle场景
    // startOf('day') 精确到该月第一天的0点，防止hour影响造成误差
    return [
      currentStartDate.subtract(startNum, 'weeks').startOf('isoWeek').startOf('day'),
      currentEndDate.subtract(endNum, 'weeks').endOf('isoWeek').startOf('day')
    ];
  }

  if (timeType?.toLocaleLowerCase() === 'month') {
    if (isLastDateMonth) {
      const monthStartVal = currentStartDate.subtract(lastDateStartNum, 'days');
      const monthEndVal = currentEndDate.subtract(lastDateEndNum, 'days');
      return [monthStartVal.startOf('month').startOf('day'), monthEndVal.endOf('month').startOf('day')];
    }
    return [
      currentStartDate.subtract(startNum, 'months').startOf('month').startOf('day'),
      currentEndDate.subtract(endNum, 'months').endOf('month').startOf('day')
    ];
  }

  return isSingle ? currentStartDate.subtract(singleNum, 'days').format('YYYY-MM-DD')
    : [currentStartDate.subtract(startNum, 'days'), currentEndDate.subtract(endNum, 'days')];
};

const StudioMetricsCardTitle: React.FC<MetricsCardWrapTitleProps> = (props) => {
  const { className, style, titleEn, titleCn, subTitleEn, subTitleCn, imgClassName, timeType = 'Day', children, currentGame, filters, startNum = 0, endNum = 0, lastDate, payType, ...restProps } = props;
  const { locale } = useIntl(); // 中英切换方法
  const isEN = locale === LANGUAGE_EN;
  const timeZone = parseInt(currentGame?.utc_offset || '', 10) || 0;
  const filter = useMemo(() => {
    const map = {
      Day: getDateDefault({ timeType: 'day', timeZone, startNum, endNum, lastDate }),
      Week: getDateDefault({ timeType: 'week', timeZone, startNum, endNum, lastDate }),
      Month: getDateDefault({ timeType: 'month', timeZone, startNum, endNum, lastDate }),
    };
    const rangeTime = map[timeType] || map?.['Day'];
    const startTime = moment(rangeTime?.[0])?.format('YYYY-MM-DD');
    const endTime = moment(rangeTime?.[1])?.format('YYYY-MM-DD');
    const jumpTimeType = timeType === 'Day' ? 'date' : timeType?.toLocaleLowerCase();
    // 调整默认日期继承逻辑，跳转Salesoverview同D/W/MAU逻辑
    const componentDefault: any =
      commonDateDefault(timeZone, undefined, undefined, { dayStartOffset: 1, weekStartOffset: 1, monthStartOffset: 1, })?.componentDefault || {};

    const jumpTime = componentDefault?.[jumpTimeType];
    const filter = {
      start_time: startTime,
      end_time: endTime,
      // 接口获取的lastDate最新有数据的时间点
      last_date: lastDate,
      timeType,
      rangeTime,
      jumpTime,
      timeZone,
      date_type: PERIODTIMEOBJ?.[timeType] || 'daily',
      isSingle: true,
      payType,
      filters: filters || {
        "zone_list": ["255"],
        "market_list": ["255"],
        "channel_list": ["255"],
        "os_list": ["255"],
        "lang_list": ["255"],
      },
    };
    return filter;
  }, [filters, timeType, timeZone, lastDate, payType]);

  const { subTitle, timeTitle } = useMemo(() => {
    const subTitle = getLanguage(isEN, subTitleEn, subTitleCn) || '';
    const timeTitle = timeType === 'Day' ? `(${filter?.last_date})` : `(${filter?.start_time} ~ ${filter?.last_date})`;
    return { subTitle, timeTitle };
  }, [filter, isEN, subTitleCn, subTitleEn, timeType]);

  return <div className={classnames(className)} style={style}>
    <div className='dbh-component-title-flex'>
      <div className='dbh-component-title'>
        <MetricsCardWrapTitle title={getLanguage(isEN, titleEn, titleCn)} className={imgClassName} {...restProps} />
        <div className='dbh-component-time-title'>{timeTitle}</div>
      </div>
      <div className='dbh-component-sub-title'>{subTitle}</div>
    </div>
    {(
      children &&
      React.Children.map(children, (child) => React.cloneElement(child, { filter }))
    )}
  </div>;
};

export default StudioMetricsCardTitle;
